<script setup lang="ts">
import { RouterView } from "vue-router";
import { computed, onErrorCaptured, onMounted, watch } from "vue";
import { isConnected } from "luminary-shared";
import { isAppLoading, userPreferencesAsRef, localCacheVersion } from "./globalConfig";
import { useNotificationStore } from "./stores/notification";
import { ArrowLeftEndOnRectangleIcon, SignalSlashIcon } from "@heroicons/vue/20/solid";
import * as Sentry from "@sentry/vue";
import { useRouter } from "vue-router";
import PrivacyPolicyModal from "@/components/navigation/PrivacyPolicyModal.vue";
import SearchModal from "@/components/navigation/SearchModal.vue";
import MobileMenu from "@/components/navigation/MobileMenu.vue";
import { useMobileChromeAutoHide } from "@/composables/useMobileChromeAutoHide";
import AffinityDebugOverlay from "@/components/debug/AffinityDebugOverlay.vue";
import { affinityDebugEnabled, applyAffinityDebugQuery } from "@/recommendation/affinityDebug";
import { useAuthWithPrivacyPolicy } from "@/composables/useAuthWithPrivacyPolicy";
import { showProviderSelectionModal } from "@/auth";
import AuthProviderSelectionModal from "@/components/authProvider/AuthProviderSelectionModal.vue";
import { useI18n } from "vue-i18n";
import { usePwaUpdate } from "@/composables/usePwaUpdate";
import { useHydrated } from "@/composables/useHydrated";

const { t } = useI18n();
const { needRefresh, reload } = usePwaUpdate();
const mobileChrome = useMobileChromeAutoHide();

const router = useRouter();

// Affinity debug overlay — hidden by default, switched on via ?affinityDebug (no other gate,
// so it works on any deployed environment, not just local dev). The flag is held in the
// session, not read off the route, so the overlay outlives the navigation that set it.
watch(() => router.currentRoute.value.query.affinityDebug, applyAffinityDebugQuery, {
    immediate: true,
});
const {
    isAuthenticated,
    user,
    loginWithRedirect,
    isPrivacyPolicyAccepted,
    showPrivacyPolicyModal,
    completePendingLogin,
    cancelPendingLogin,
} = useAuthWithPrivacyPolicy();

// Watch for privacy policy acceptance to complete pending login
watch(isPrivacyPolicyAccepted, (accepted) => {
    if (accepted) {
        completePendingLogin();
    }
});

// Handle modal close
const handleModalClose = () => {
    cancelPendingLogin();
};

watch(needRefresh, (refreshNeeded) => {
    if (!refreshNeeded) return;
    useNotificationStore().addNotification({
        id: "updateBanner",
        title: () => t("notification.update_available.title"),
        description: () => t("notification.update_available.description"),
        state: "info",
        type: "banner",
        link: reload,
        closable: false,
        priority: 0,
    });
});

// Gate client-only chrome (this offline-banner watch, and the auth-gate reveal further
// down) behind mount so the web/SSG first client render matches the prerendered HTML
// (clean hydration) — isConnected never changes during the Node prerender (no socket),
// so an ungated watch here would leak an idle timer/watch for the life of the whole SSG
// build. The normal SPA has no prerender, so this is a no-op there.
const isMounted = useHydrated();

watch(
    isMounted,
    (mounted) => {
        if (!mounted) return;

        // Clear the banner — including one restored from the previous page load — the
        // moment the socket is up, rather than waiting out the grace period below, which
        // would leave a stale offline banner on screen for a reload that reconnects fine.
        watch(
            isConnected,
            (connected) => {
                if (connected) useNotificationStore().removeNotification("offlineBanner");
            },
            { immediate: true },
        );

        // Wait 5 seconds to allow the socket connection to be established before checking the connection status
        setTimeout(() => {
            watch(
                isConnected,
                (connected) => {
                    if (connected) return;
                    useNotificationStore().addNotification({
                        id: "offlineBanner",
                        title: () => t("notification.offline.title"),
                        description: () => t("notification.offline.message"),
                        state: "warning",
                        type: "banner",
                        icon: SignalSlashIcon,
                        priority: 1,
                        // Staying offline outlives the page, so a reload should say so
                        // straight away instead of after the grace period above.
                        persist: true,
                    });
                },
                { immediate: true },
            );
        }, 5000);
    },
    { immediate: true },
);

watch(
    [isConnected, isAuthenticated],
    () => {
        if (isConnected.value && !isAuthenticated.value) {
            useNotificationStore().addNotification({
                id: "accountBanner",
                title: () => t("notification.login.title"),
                description: () => t("notification.login.message"),
                state: "warning",
                type: "banner",
                icon: ArrowLeftEndOnRectangleIcon,
                link: () => loginWithRedirect(),
                closable: false,
            });
        }

        if (!isConnected.value || isAuthenticated.value) {
            useNotificationStore().removeNotification("accountBanner");
        }
    },
    { immediate: true },
);

// Add userId to analytics if privacy policy has been accepted
const unwatchUserPref = watch(userPreferencesAsRef.value, () => {
    if (userPreferencesAsRef.value.privacyPolicy?.status == "accepted") {
        if (isAuthenticated.value) {
            // @ts-expect-error window is a native browser api, and matomo is attaching _paq to window
            window._paq && user && user.value && window._paq.push(["setUserId", user.value.email]);
        }
    }

    // Stop watcher if the privacy policy is accepted or declined
    if (userPreferencesAsRef.value.privacyPolicy?.status) unwatchUserPref();
});

const routeKey = computed(() => {
    const route = router.currentRoute.value;
    // Search keeps one instance whatever its ?q, so the search survives leaving the page.
    if (typeof route.name === "string" && /^search(-|$)/.test(route.name)) return route.path;
    return route.fullPath;
});

onMounted(() => {
    // Reveal content hidden by vite.config.web.ts's pre-paint auth gate (see
    // authGateScript there for why): by now Vue's first render has landed, using the
    // auth-scoped response cache, so it's never the wrong (public) content. No-op when
    // the gate never engaged (logged-out reload, or the normal SPA build).
    document.documentElement.classList.remove("ssg-auth-pending");
});

onErrorCaptured((err) => {
    console.error(err);
    // Sentry's browser SDK isn't initialised during the SSG prerender (and its
    // capture fns may be absent), so guard the call.
    if (typeof Sentry?.captureException === "function") Sentry.captureException(err);
});
</script>

<template>
    <!-- The startup splash is plain HTML in index.html, so it can paint before this
         component (or any module) exists. This gate only withholds the app until
         startup finishes; index.html's copy is what covers the wait. -->
    <div
        v-if="!isAppLoading"
        class="absolute bottom-0 left-0 right-0 top-0 flex w-full flex-col overflow-hidden"
    >
        <div class="flex-1 overflow-y-scroll scrollbar-hide">
            <RouterView v-slot="{ Component }">
                <!-- :key on KeepAlive — bumping localCacheVersion (on clear-local-cache) discards the
                     cached page instances so Home/Explore/Watch/Search re-create from the purged cache. -->
                <KeepAlive
                    :key="localCacheVersion"
                    include="HomePage,ExplorePage,VideoPage,SearchPage"
                >
                    <component
                        :is="Component"
                        :key="routeKey"
                    />
                </KeepAlive>
            </RouterView>
        </div>

        <!-- Bottom menu divider for mobile view -->
        <!-- <div class="w-full lg:hidden h-[2px] bg-zinc-100/25 dark:bg-slate-700/50"></div> -->
        <!-- Mobile Navigation (mobile only) -->
        <!-- Overlays the content and steps aside via transform while a reading page
             scrolls down (see useMobileChromeAutoHide): a transform animates on the
             compositor and never resizes the page mid-scroll, where a layout
             animation reads as the text jumping. Content clears the bar through
             --mobile-menu-h padding. -->
        <div
            class="absolute inset-x-0 bottom-0 z-50 transition-transform duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] will-change-transform lg:hidden"
            :class="mobileChrome.hidden.value ? 'translate-y-full' : 'translate-y-0'"
        >
            <MobileMenu class="w-full border-t-2 border-t-zinc-100/25 dark:border-t-slate-700/50" />
        </div>

        <!-- Privacy Policy Modal for authentication flow -->
        <PrivacyPolicyModal
            v-if="isMounted"
            v-model:show="showPrivacyPolicyModal"
            @close="handleModalClose"
        />

        <AffinityDebugOverlay v-if="isMounted && affinityDebugEnabled" />
    </div>
    <!-- Modals depend on i18n, which isn't installed until splash finishes — keep them out of the tree during the loading phase. On web they are also gated behind mount (signed-out shell). -->
    <template v-if="!isAppLoading && isMounted">
        <SearchModal />
        <AuthProviderSelectionModal v-model:isVisible="showProviderSelectionModal" />
    </template>
</template>
